<div class="ai-admin-page space-y-4 px-4 py-4">
  <div class="rounded-2xl border border-slate-200 bg-gradient-to-r from-emerald-50 via-white to-cyan-50 p-4">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div>
        <div class="text-xs uppercase tracking-wide text-slate-500">Control Center</div>
        <div class="text-xl font-semibold text-slate-800">AI Administration</div>
        <div class="text-sm text-slate-600">Operational controls for index health, cleanup, and diagnostics.</div>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <button pButton type="button" icon="pi pi-home" label="Home" class="p-button-text" (click)="goHome()"></button>
      </div>
    </div>
  </div>

  <div class="rounded-2xl border border-slate-200 bg-white p-2">
    <div class="flex flex-wrap gap-2 px-2 pt-2">
      <button
        type="button"
        class="rounded-lg px-3 py-2 text-sm"
        [ngClass]="activeTab === 'overview' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'"
        (click)="setTab('overview')">
        Overview
      </button>
      <button
        type="button"
        class="rounded-lg px-3 py-2 text-sm"
        [ngClass]="activeTab === 'maintenance' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'"
        (click)="setTab('maintenance')">
        Maintenance
      </button>
      <button
        type="button"
        class="rounded-lg px-3 py-2 text-sm"
        [ngClass]="activeTab === 'debug' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'"
        (click)="setTab('debug')">
        Debug
      </button>
    </div>

    <!-- Overview tab -->
    <div class="space-y-4 p-2 pt-4" *ngIf="activeTab === 'overview'">
      <div class="flex flex-wrap gap-2">
        <button
          pButton
          type="button"
          label="Refresh Diagnostics"
          icon="pi pi-refresh"
          [loading]="diagnosticsLoading"
          (click)="onRefreshDiagnosticsClick()"></button>
      </div>

      <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div class="rounded border border-slate-200 bg-slate-50 p-3">
          <div class="text-xs uppercase tracking-wide text-slate-500">Indexed Note Count</div>
          <div class="text-2xl font-semibold text-slate-800">{{ indexedNoteCount }}</div>
        </div>
        <div class="rounded border border-slate-200 bg-slate-50 p-3">
          <div class="text-xs uppercase tracking-wide text-slate-500">Last Full Reindex</div>
          <div class="text-sm font-semibold text-slate-800 break-all">{{ lastFullReindexTime }}</div>
        </div>
        <div class="rounded border border-slate-200 bg-slate-50 p-3">
          <div class="text-xs uppercase tracking-wide text-slate-500">Last Indexing Error</div>
          <div class="text-sm font-semibold text-slate-800 break-words">{{ lastIndexingError }}</div>
        </div>
      </div>

      <div class="rounded border border-slate-200 bg-slate-50 p-3 space-y-3">
        <div class="flex items-center justify-between gap-2">
          <div class="text-sm font-medium text-slate-700">Last 10 Index Operations</div>
          <button
            pButton
            type="button"
            label="Refresh Entries"
            icon="pi pi-refresh"
            size="small"
            (click)="refreshIndexHistory()"></button>
        </div>

        <div class="overflow-x-auto" *ngIf="recentIndexEntries.length > 0; else noIndexEntries">
          <table class="min-w-full text-xs text-slate-700">
            <thead>
              <tr class="border-b border-slate-200 text-left">
                <th class="py-2 pr-3">Time</th>
                <th class="py-2 pr-3">Kind</th>
                <th class="py-2 pr-3">Status</th>
                <th class="py-2 pr-3">Job ID</th>
                <th class="py-2 pr-3">Note ID</th>
                <th class="py-2 pr-3">Title</th>
                <th class="py-2 pr-3">Details</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let entry of recentIndexEntries" class="border-b border-slate-100 align-top">
                <td class="py-2 pr-3 whitespace-nowrap">{{ entry.finished_at || entry.started_at || entry.submitted_at | date:'medium' }}</td>
                <td class="py-2 pr-3 whitespace-nowrap">{{ entry.job_type }}</td>
                <td class="py-2 pr-3 whitespace-nowrap">
                  <span [ngClass]="{
                    'text-emerald-700': entry.status === 'success' || entry.status === 'completed',
                    'text-amber-700': entry.status === 'completed_with_errors',
                    'text-blue-700': entry.status === 'running' || entry.status === 'accepted' || entry.status === 'in_progress',
                    'text-rose-700': entry.status === 'failed'
                  }">{{ entry.status }}</span>
                </td>
                <td class="py-2 pr-3 break-all">{{ entry.job_id || '-' }}</td>
                <td class="py-2 pr-3 break-all">{{ entry.note_id || '-' }}</td>
                <td class="py-2 pr-3 break-words">{{ entry.note_title || entry.note_id || '-' }}</td>
                <td class="py-2 pr-3 break-words">
                  <ng-container *ngIf="entry.job_type === 'bulk'">
                    {{ entry.processed_count }}/{{ entry.total_docs_in_database }} chunks: {{ entry.chunks_indexed }}
                  </ng-container>
                  <ng-container *ngIf="entry.job_type === 'note'">
                    chunks: {{ entry.chunks_indexed }}
                  </ng-container>
                  <ng-container *ngIf="entry.error">
                    {{ entry.error }}
                  </ng-container>
                  <ng-container *ngIf="!entry.error && entry.job_type !== 'bulk' && entry.job_type !== 'note'">
                    -
                  </ng-container>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ng-template #noIndexEntries>
          <div class="text-xs text-slate-500">No index operations recorded yet.</div>
        </ng-template>
      </div>
    </div>

    <!-- Maintenance tab -->
    <div class="space-y-4 p-2 pt-4" *ngIf="activeTab === 'maintenance'">
      <div class="flex flex-wrap items-center gap-2">
        <label class="flex items-center gap-2 text-sm">
          <span>Reindex limit</span>
          <input
            pInputText
            type="number"
            min="1"
            class="w-28"
            placeholder="all notes"
            [(ngModel)]="reindexLimit" />
        </label>

        <button
          pButton
          type="button"
          label="Reindex Notes"
          icon="pi pi-play"
          [loading]="reindexing"
          (click)="reindexNotes()"></button>

        <button
          pButton
          type="button"
          label="Clear Index"
          icon="pi pi-trash"
          severity="danger"
          [loading]="clearing"
          (click)="clearIndex()"></button>

        <button
          pButton
          type="button"
          label="Refresh Status"
          icon="pi pi-refresh"
          class="p-button-secondary"
          (click)="refreshActiveReindexStatus()"></button>
      </div>

      <div class="rounded border border-slate-200 bg-slate-50 p-3">
        <div class="mb-2 text-sm font-medium text-slate-700">Single Note Index Operations</div>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_auto] md:items-end">
          <label class="block">
            <span class="text-xs text-slate-600">Note ID</span>
            <input
              pInputText
              type="text"
              class="mt-1 w-full"
              placeholder="Enter note ID"
              [(ngModel)]="indexOperationNoteId" />
          </label>

          <button
            pButton
            type="button"
            label="Create Embeddings"
            icon="pi pi-plus"
            [loading]="creatingSingleNoteIndex"
            [disabled]="!indexOperationNoteId.trim() || deletingSingleNoteIndex"
            (click)="createEmbeddingsForSingleNote()"></button>

          <button
            pButton
            type="button"
            label="Delete Embeddings"
            icon="pi pi-times"
            severity="danger"
            [loading]="deletingSingleNoteIndex"
            [disabled]="!indexOperationNoteId.trim() || creatingSingleNoteIndex"
            (click)="deleteEmbeddingsForSingleNote()"></button>
        </div>
      </div>

      <div class="rounded border border-slate-200 bg-slate-50 p-3" *ngIf="activeReindexJobId">
        <div class="mb-2 text-sm font-medium text-slate-700">Reindex Job Progress</div>
        <div class="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
          <div><span class="font-medium">Job ID:</span> {{ activeReindexJobId }}</div>
          <div>
            <span class="font-medium">Status:</span>
            <span
              class="font-semibold"
              [ngClass]="{
                'text-blue-700': activeReindexJobStatus === 'accepted' || activeReindexJobStatus === 'running' || activeReindexJobStatus === 'in_progress',
                'text-emerald-700': activeReindexJobStatus === 'completed',
                'text-amber-700': activeReindexJobStatus === 'completed_with_errors',
                'text-rose-700': activeReindexJobStatus === 'failed'
              }">
              {{ activeReindexJobStatus || '-' }}
              <span *ngIf="isJobStalled" class="text-xs font-normal text-red-600 ml-2">[STALLED: 30+ seconds no activity]</span>
            </span>
          </div>
          <div *ngIf="activeReindexRequestedLimit !== null"><span class="font-medium">Requested:</span> {{ activeReindexRequestedLimit }} notes</div>
          <div *ngIf="activeReindexRequestedLimit === null && activeReindexJobId"><span class="font-medium">Requested:</span> all notes (no limit)</div>
          <div *ngIf="activeReindexStartedAt"><span class="font-medium">Started:</span> {{ activeReindexStartedAt | date:'medium' }}</div>
          <div *ngIf="activeReindexLastUpdated"><span class="font-medium">Last Update:</span> {{ activeReindexLastUpdated | date:'mediumTime' }}</div>

          <div class="md:col-span-2" *ngIf="totalDocsInDatabase > 0">
            <span class="font-medium">Progress:</span>
            <span class="text-lg font-bold" [ngClass]="activeReindexProgressPercent && activeReindexProgressPercent >= 100 ? 'text-emerald-700' : 'text-blue-700'">
              {{ activeReindexProgressPercent || 0 }}%
            </span>
            <span class="text-xs text-slate-600 ml-2">({{ docsProcessed }} / {{ totalDocsInDatabase }} notes processed)</span>
            <span *ngIf="isReindexFinalizing()" class="text-xs text-amber-700 ml-2">indexing chunks...</span>
          </div>

          <div class="md:col-span-2" *ngIf="totalDocsInDatabase > 0 && activeReindexProgressPercent !== null">
            <div class="h-3 overflow-hidden rounded bg-slate-300 border border-slate-400">
              <div
                class="h-full transition-all duration-500"
                [ngClass]="activeReindexProgressPercent && activeReindexProgressPercent >= 100 ? 'bg-emerald-600' : 'bg-blue-600'"
                [style.width.%]="activeReindexProgressPercent"></div>
            </div>
          </div>

          <div class="md:col-span-2" *ngIf="activeReindexSummary">
            <span class="font-medium">Details:</span> {{ activeReindexSummary }}
          </div>
        </div>
      </div>

      <div *ngIf="getPendingJobsArray().length > 0" class="rounded border border-yellow-200 bg-yellow-50 p-3 space-y-2">
        <div class="text-sm font-medium text-yellow-900">Pending Single-Note Reindex Jobs</div>
        @for (item of getPendingJobsArray(); track item.noteId) {
          <div class="rounded border border-yellow-100 bg-white p-2 text-xs">
            <div class="flex items-start justify-between gap-2 mb-1">
              <div class="font-medium text-slate-700">{{ item.noteId }}</div>
              <button
                type="button"
                (click)="clearPendingJob(item.noteId)"
                class="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>
            <div class="grid grid-cols-2 gap-1 text-slate-600 mb-2">
              <div>Status: <span class="font-medium" [ngClass]="{
                'text-yellow-600': item.job.status === 'queued' || item.job.status === 'verifying' || item.job.status === 'pending_backend',
                'text-emerald-600': item.job.status === 'indexed',
                'text-rose-600': item.job.status === 'verify_failed' || item.job.status === 'backend_timeout'
              }">{{ item.job.status }}</span></div>
              <div>Chunks: {{ item.job.chunks }}</div>
              <div>Checks: {{ item.job.attempts || 0 }}/{{ item.job.maxAttempts || 0 }}</div>
            </div>
            @if (item.job.status !== 'indexed') {
              <button
                type="button"
                pButton
                label="Verify Now"
                size="small"
                (click)="verifySingleNoteEmbeddings(item.noteId)"
                class="text-xs"></button>
            }
          </div>
        }
      </div>
    </div>

    <!-- Debug tab — AI Index Inspector -->
    <div class="p-2 pt-4" *ngIf="activeTab === 'debug'">
      <app-ai-index-inspector></app-ai-index-inspector>
    </div>

    <div *ngIf="message" class="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
      {{ message }}
    </div>

    <div *ngIf="error" class="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
      {{ error }}
    </div>
  </div>

</div>
